import { useCallback, useEffect, useRef, useState } from 'react';

/** Most-recent-first; older entries fall off the end. */
export const RECENT_REPORTS_LIMIT = 10;

const storageKey = (pageName: string) => `cms2.recentReports.${pageName}`;

/**
 * localStorage is deliberate: recency is a per-device convenience, not shared
 * state, and keeping it client-side avoids a schema change for it.
 */
const read = (pageName: string): string[] => {
  try {
    const raw = window.localStorage.getItem(storageKey(pageName));
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    // Anything could be in storage — another tab, an older build, a user with
    // devtools open. Validate rather than trust it into the UI.
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((c): c is string => typeof c === 'string').slice(0, RECENT_REPORTS_LIMIT);
  } catch {
    // Private-mode quota errors, disabled storage, or malformed JSON.
    return [];
  }
};

const write = (pageName: string, codes: string[]) => {
  try {
    window.localStorage.setItem(storageKey(pageName), JSON.stringify(codes));
  } catch {
    // Recency is a convenience; failing to persist it must never break the page.
  }
};

export interface UseRecentReportsReturn {
  /** Report codes most recently opened on this page, newest first. */
  recentCodes: string[];
  /** Records a report as just-opened, moving it to the front. */
  recordRecent: (code: string) => void;
  clearRecent: () => void;
}

/**
 * Tracks which saved reports the user last opened on a given list page, so the
 * tab strip's picker can offer a short "recent" list instead of the full
 * catalogue (which now lives behind the page's Reports button).
 *
 * @param pageName report page key, e.g. 'ServiceList' — scopes the list per page.
 */
export const useRecentReports = (pageName: string): UseRecentReportsReturn => {
  const [recentCodes, setRecentCodes] = useState<string[]>(() => read(pageName));
  // Mirrors the committed list so recordRecent can compute the next value
  // outside the state updater. Updaters must stay pure — React may call one
  // twice (StrictMode) or discard it (concurrent rendering), and writing to
  // localStorage from inside would run on a render React threw away.
  const latest = useRef(recentCodes);

  // Re-read when the page changes, so a shared component can't carry one page's
  // recents into another.
  useEffect(() => {
    const stored = read(pageName);
    latest.current = stored;
    setRecentCodes(stored);
  }, [pageName]);

  const recordRecent = useCallback(
    (code: string) => {
      if (!code) return;
      const prev = latest.current;
      const next = [code, ...prev.filter((c) => c !== code)].slice(0, RECENT_REPORTS_LIMIT);
      // Already at the front: keep the same reference so re-opening the active
      // report doesn't re-render every consumer.
      if (prev.length === next.length && prev.every((c, i) => c === next[i])) return;
      latest.current = next;
      setRecentCodes(next);
      write(pageName, next);
    },
    [pageName]
  );

  const clearRecent = useCallback(() => {
    latest.current = [];
    setRecentCodes([]);
    write(pageName, []);
  }, [pageName]);

  return { recentCodes, recordRecent, clearRecent };
};
